<template>
  <div
    class="choose_location"
    :class="[{ 'block-filled': isCalc }, { 'disable-element': disableElement }]"
  >
    <div class="block-heading">
      SET START & END LOCATION
    </div>
    <div class="start-end">
      <div class="start">
        <google-autocomplete
          v-if="startLocationType === '1'"
          :key="inputStartElement.id"
          :input-element="inputStartElement"
          @input-changed="setCustomStart"
          @google-location-selected="googleLocationSelected"
        />
        <v-autocomplete
          v-else
          v-model="startpoint"
          :disabled="disableStartEnd"
          :items="startLocationList"
          item-text="Address"
          label="Start Location: *"
          return-object
          @change="$emit('on-change', { data: $event, type: 'start' })"
        />
        <div />
        <v-radio-group
          id="startLocationType"
          v-model="startLocationType"
          row
          :disabled="disableStartEnd"
          hide-details
          @change="startLocationSelector"
        >
          <v-radio label="from homes" value="0" />
          <v-radio label="custom address" value="1" />
        </v-radio-group>
      </div>
      <div class="end" :class="{ 'disable-element': !isStartFilled }">
        <google-autocomplete
          v-if="endLocationType === '1'"
          :key="inputEndElement.id"
          :input-element="inputEndElement"
          @input-changed="setCustomEnd"
          @google-location-selected="googleLocationSelected"
        />
        <v-autocomplete
          v-else
          v-model="endpoint"
          :items="endLocationList"
          item-text="Address"
          label="End Location:"
          return-object
          @change="$emit('on-change', { data: $event, type: 'end' })"
        />
        <div />
        <v-radio-group
          id="endLocationType"
          v-model="endLocationType"
          row
          :disabled="disableStartEnd"
          hide-details
          @change="endLocationSelector"
        >
          <v-radio
            label="from homes"
            value="0"
            :class="{ 'disable-element': disableEndSelector }"
          />
          <v-radio label="custom address" value="1" />
        </v-radio-group>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import GoogleAutocomplete from './GoogleAutocomplete.vue'
import { isEmpty } from '../../../../utils/generalUtils'
import {
  isPastRoute,
  getStartLocationAddressText,
  getEndLocationAddressText
} from '../../../../utils/routeUtils'
import {
  // homeStopsArray,
  isCustomStopCalculated,
  isCustomStartExist,
  isCustomEndExist,
  hasCustomStartStop,
  hasCustomEndStop,
  hasMoreThanOneStop
} from '../../../../utils/planRouteUtils'

export default {
  components: { GoogleAutocomplete },

  props: {
    route: {
      type: Object,
      required: true
    },
    startLocationList: {
      type: Array,
      required: true
    },
    firstBlockCompleted: {
      type: Boolean,
      default: false
    },
    clientChanged: {
      type: Boolean,
      required: true
    },
    resetStartEnd: {
      type: Boolean,
      default: false
    },
    setStartEnd: {
      type: Boolean,
      default: true
    },
    resetEndLocation: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      startLocationType: '0',
      endLocationList: [],
      endLocationType: this.disableEndSelector ? '1' : '0',
      startpoint: null,
      endpoint: null,
      preStart: '',
      preEnd: '',
      autoService: null,
      isStartFilled: false,
      isEndFilled: false
    }
  },

  computed: {
    ...mapState('agent', [
      'selectedStartLocation',
      'selectedEndLocation',
      'routeEditMode'
    ]),
    ...mapState('common', ['selectedRoute']),

    inputStartElement() {
      return {
        id: 'startBox',
        placeholder: 'Start Location: *',
        disabled: this.disableStartEnd,
        preValue: this.preStart
      }
    },

    inputEndElement() {
      return {
        id: 'endBox',
        placeholder: 'End Location: *',
        disabled: this.disableStartEnd,
        preValue: this.preEnd
      }
    },

    disableElement() {
      return (
        isPastRoute(this.selectedRoute) ||
        !this.firstBlockCompleted ||
        !this.clientChanged
      )
    },

    disableStartEnd() {
      return isEmpty(this.route) || isEmpty(this.route.calculatedArray)
    },

    disableEndSelector() {
      return isEmpty(this.endLocationList)
    },

    isCalc() {
      if (hasMoreThanOneStop(this.route)) {
        return this.isStartFilled && this.isEndFilled
      }
      if (!this.isStartFilled) this.$emit('start-end-state', false)
      return this.isStartFilled
    }
  },

  watch: {
    resetEndLocation(value) {
      if (value) {
        this.endpoint = null
        this.preEnd = ''
        this.$emit('reset-end-location')
      }
    },

    route: {
      handler: function(value) {
        this.initStartEndValues(value)
      },
      deep: true
    },

    startLocationList: {
      handler: function(value) {
        if (!isEmpty(value)) {
          this.endLocationList =
            this.startLocationType === '0'
              ? value.filter(item => item.propertyId !== value[0].propertyId)
              : [...value]
        }
      },
      deep: true
    },

    startLocationType(value) {
      this.isStartFilled =
        value === '0' ? !isEmpty(this.startpoint) : !isEmpty(this.preStart)
      if (!isEmpty(this.startLocationList)) {
        this.endLocationList =
          value === '0'
            ? this.startLocationList.filter(
              item => item.propertyId !== this.startLocationList[0].propertyId
            )
            : [...this.startLocationList]
      }
    },

    endLocationList: {
      handler: function(value) {
        if (isEmpty(value)) this.endLocationType = '1'
      }
    },

    endLocationType(value) {
      this.isEndFilled =
        value === '0' ? !isEmpty(this.endpoint) : !isEmpty(this.preEnd)
    },

    preStart(value) {
      this.isStartFilled = !(this.startLocationType === '1' && isEmpty(value))
    },

    preEnd(value) {
      if (hasMoreThanOneStop(this.route)) {
        this.isEndFilled = !(this.endLocationType === '1' && isEmpty(value))
      } else {
        this.isEndFilled = false
      }
    },

    startpoint(value) {
      this.isStartFilled = !(this.startLocationType === '0' && isEmpty(value))
    },

    endpoint(value) {
      if (hasMoreThanOneStop(this.route)) {
        this.isEndFilled = !(this.endLocationType === '0' && isEmpty(value))
      } else {
        this.isEndFilled = false
      }
    },

    disableEndSelector(value) {
      if (value) {
        this.endLocationType = '1'
      }
    },

    isCalc(value) {
      this.$emit('start-end-state', value)
    }
  },

  mounted() {
    this.initStartEndValues(this.route)
  },

  methods: {
    ...mapMutations('agent', {
      setSelectedStartLocation: 'SET_SELECTED_START_LOCATION',
      setSelectedEndLocation: 'SET_SELECTED_END_LOCATION'
    }),

    initStartEndValues(routeData) {
      if (!isEmpty(routeData) && (!this.routeEditMode || this.setStartEnd)) {
        if (!isEmpty(routeData.calculatedArray)) {
          const markers = [...this.route.calculatedArray]
          if (markers.length > 1) {
            const lastHome = markers[markers.length - 1]
            this.setStartLocation(markers[0])
            this.setEndLocation(lastHome)
          } else if (markers.length > 0) {
            this.setStartLocation(markers[0])
            this.preEnd = ''
          }
          if (isCustomStartExist(this.selectedStartLocation, markers)) {
            markers.unshift({ ...this.selectedStartLocation })
          }
          if (isCustomEndExist(this.selectedEndLocation, markers)) {
            markers.push({ ...this.selectedEndLocation })
          }
        } else {
          this.preStart = ''
          this.preEnd = ''
        }
        this.$emit('set-start-end')
      } else if (isEmpty(routeData)) {
        this.startpoint = null
        this.endpoint = null
        this.preStart = ''
        this.preEnd = ''
      } else if (this.resetStartEnd) {
        this.startpoint = null
        this.endpoint = null
        this.preStart = ''
        this.preEnd = ''
        this.$emit('reset-start-end')
      }
    },

    // Function to check if google location is selected from dropdown
    // if it is then add it to home list and update route calculated array
    googleLocationSelected(data) {
      const updatedCalculatedArray = [...this.route.calculatedArray]
      const { type, isGoogleLocation, selectedPlace } = data
      if (isGoogleLocation) {
        if (type === 'start') {
          this.preStart = selectedPlace.Address
          if (!hasCustomStartStop(updatedCalculatedArray)) {
            const customProperty = {
              ...selectedPlace,
              Address: `Custom Start Location: ${selectedPlace.Address}`
            }
            updatedCalculatedArray.unshift(customProperty)
            this.setSelectedStartLocation(customProperty)
          }
        } else {
          this.preEnd = selectedPlace.Address
          if (!hasCustomEndStop(updatedCalculatedArray)) {
            const customProperty = {
              ...selectedPlace,
              Address: `Custom End Location: ${selectedPlace.Address}`
            }
            updatedCalculatedArray.push(customProperty)
            this.setSelectedEndLocation(customProperty)
          }
        }
        this.$emit('activate-route-edit-mode', updatedCalculatedArray)
      }
    },

    setStartLocation(home) {
      if (home.propertyId) {
        this.startLocationType = '0'
        this.startpoint = home
      } else {
        if (!this.routeEditMode) this.startLocationType = '1'
        this.preStart = getStartLocationAddressText(home.Address)
      }
    },

    setEndLocation(home) {
      if (home.propertyId) {
        this.endLocationType = '0'
        this.endpoint = home
      } else {
        if (!this.routeEditMode) this.endLocationType = '1'
        this.preEnd = getEndLocationAddressText(home.Address)
      }
    },

    isStartEnd() {
      return (
        !isEmpty(this.startpoint || this.preStart) &&
        !isEmpty(this.selectedEndLocation.text)
      )
    },

    startLocationSelector(event) {
      const updatedCalculatedArray = [...this.route.calculatedArray]
      if (event === '1') {
        if (
          isCustomStartExist(this.selectedStartLocation, updatedCalculatedArray)
        ) {
          updatedCalculatedArray.unshift({ ...this.selectedStartLocation })
        } else {
          if (!isCustomStopCalculated(this.selectedStartLocation)) {
            this.preStart = ''
            this.setSelectedStartLocation({})
          }
        }
      } else {
        // const stops = homeStopsArray(this.route.calculatedArray)
        // if (!isEmpty(stops)) {
        //   this.startpoint = stops[0]
        // } else {
        //   this.startpoint = null
        // }
        if (hasCustomStartStop(updatedCalculatedArray)) {
          updatedCalculatedArray.shift()
        }
      }
      this.$emit('activate-route-edit-mode', updatedCalculatedArray)
    },

    endLocationSelector(event) {
      const updatedCalculatedArray = [...this.route.calculatedArray]
      if (event === '1') {
        if (
          isCustomEndExist(this.selectedEndLocation, updatedCalculatedArray)
        ) {
          updatedCalculatedArray.push({ ...this.selectedEndLocation })
        } else {
          if (!isCustomStopCalculated(this.selectedEndLocation)) {
            this.preEnd = ''
            this.setSelectedEndLocation({})
          }
        }
      } else {
        // const stops = homeStopsArray(this.route.calculatedArray)
        // if (stops && stops.length > 1) {
        //   this.endpoint = stops[stops.length - 1]
        // } else {
        //   this.endpoint = null
        // }
        if (hasCustomEndStop(updatedCalculatedArray)) {
          updatedCalculatedArray.pop()
        }
      }
      this.$emit('activate-route-edit-mode', updatedCalculatedArray)
    },

    setCustomStart(event) {
      this.preStart = event.target.value
      this.setSelectedStartLocation({
        Address: event.target.value,
        arrivalTime: null,
        latitude: null,
        longitude: null,
        propertyId: null
      })
      const calculatedArray = [...this.route.calculatedArray]
      if (hasCustomStartStop(calculatedArray)) {
        calculatedArray.shift()
      }
      this.$emit('activate-route-edit-mode', calculatedArray)
    },

    setCustomEnd(event) {
      this.preEnd = event.target.value
      this.setSelectedEndLocation({
        Address: event.target.value,
        arrivalTime: null,
        latitude: null,
        longitude: null,
        propertyId: null
      })
      const calculatedArray = [...this.route.calculatedArray]
      if (hasCustomEndStop(calculatedArray)) {
        calculatedArray.pop()
      }
      this.$emit('activate-route-edit-mode', calculatedArray)
    }
  }
}
</script>

<style lang="sass">
#endLocationType, #startLocationType, #viewingType
  label
    font-size: 1.2rem !important
  .v-input--selection-controls
    &__input
      height: 16px
      width: 16px
      margin-right: 0.2rem
  .v-icon
    font-size: 16px !important

.start-end .v-input--selection-controls
  margin-top: 0 !important
  margin-bottom: 2rem !important
  label
    font-size: 1.4rem
</style>

<style lang="sass" scoped>
.choose_location
  grid-area: location
  border: 1px solid $hwayzce
  padding: calc(10% / 3) 5% 0
  text-align: center

  .v-text-field
    margin-top: 10px !important

  .start-end
    display: grid
    grid-template-areas: "start end"
    grid-template-columns: 1fr 1fr
    grid-column-gap: 5%
    align-items: center
    .start
      grid-area: start
    .end
      grid-area: end

.disable-element
  &.choose_location
    border: 1px solid $hwayzce
</style>
